import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, 
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Chip, Dialog, DialogTitle, 
  DialogContent, DialogActions, Alert, Snackbar, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import {
  PaymentOutlined as PaymentIcon,
  CreditCardOutlined as CreditCardIcon,
  ReceiptOutlined as ReceiptIcon,
  TrendingUpOutlined as TrendingUpIcon,
  AddOutlined as AddIcon,
  DownloadOutlined as DownloadIcon,
  AccountBalanceOutlined as BankIcon,
  SecurityOutlined as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api';

const PaymentSystem = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Payment dialog state
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    tenantId: '',
    amount: '',
    method: 'Online',
    description: 'Rent payment',
    status: 'pending'
  });

  // Edit/Delete dialog state
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  // Financial data
  const [financialData, setFinancialData] = useState({
    totalRevenue: 0,
    outstandingAmount: 0,
    collectedAmount: 0,
    monthlyTrends: [],
    paymentMethods: []
  });

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      
      // Fetch real data from API
      console.log('🔍 Fetching payments data...');
      
      // Fetch all tenants first
      const tenantsResponse = await api.get('/api/tenants');
      const tenantsData = tenantsResponse.data;
      console.log('📊 Fetched tenants:', tenantsData.length);
      setTenants(tenantsData);
      
      // Fetch all properties
      const propertiesResponse = await api.get('/api/properties');
      const propertiesData = propertiesResponse.data;
      console.log('📊 Fetched properties:', propertiesData.length);
      setProperties(propertiesData);
      
      // Fetch all payments - try the global payments endpoint
      let paymentsData = [];
      try {
        const paymentsResponse = await api.get('/api/payments');
        paymentsData = paymentsResponse.data;
        console.log('📊 Fetched payments from /api/payments:', paymentsData.length);
      } catch (error) {
        console.warn('⚠️ Failed to fetch from /api/payments, will collect from tenants');
        // If that fails, collect payments from each tenant
        for (const tenant of tenantsData) {
          try {
            const tenantPaymentsResponse = await api.get(`/api/tenant-portal/tenant/${tenant.id}/payments`);
            paymentsData = [...paymentsData, ...tenantPaymentsResponse.data];
          } catch (err) {
            console.warn(`⚠️ Failed to fetch payments for tenant ${tenant.id}`);
          }
        }
        console.log('📊 Collected tenant payments:', paymentsData.length);
      }
      
      // Enrich payment data with tenant and property names
      const enrichedPayments = paymentsData.map(payment => {
        const tenant = tenantsData.find(t => t.id === payment.tenantId);
        const tenantName = tenant ? `${tenant.firstName} ${tenant.lastName}` : 'Unknown Tenant';
        
        // Get property and unit info
        let propertyInfo = 'Unknown Property';
        if (tenant?.leaseInfo?.propertyId) {
          const property = propertiesData.find(p => p.id === tenant.leaseInfo.propertyId);
          if (property) {
            const unit = property.units?.find(u => u.id === tenant.leaseInfo.unitId);
            propertyInfo = unit ? `${property.name} - ${unit.name}` : property.name;
          }
        }
        
        return {
          ...payment,
          tenantName,
          property: propertyInfo,
          type: payment.description || 'rent',
          method: payment.method?.toLowerCase().replace(' ', '_') || 'online'
        };
      });
      
      console.log('✅ Enriched payments:', enrichedPayments.length);
      setPayments(enrichedPayments);

      // Calculate financial data from real payments
      const totalRevenue = enrichedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const collectedAmount = enrichedPayments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      const outstandingAmount = enrichedPayments
        .filter(p => p.status === 'pending' || p.status === 'overdue')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      
      setFinancialData({
        totalRevenue,
        outstandingAmount,
        collectedAmount,
        monthlyTrends: [
          { month: 'Jan', revenue: collectedAmount, expenses: 0 },
        ],
        paymentMethods: [
          { name: 'Online', value: 100, color: '#8884d8' }
        ]
      });

    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch payment data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    try {
      setLoading(true);
      
      // Validation
      if (!paymentData.tenantId || !paymentData.amount) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      // Mock Stripe payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newPayment = {
        id: `pay-${Date.now()}`,
        tenantName: tenants.find(t => t.id === paymentData.tenantId)?.name,
        property: properties.find(p => p.id === paymentData.propertyId)?.name,
        amount: parseFloat(paymentData.amount),
        type: paymentData.type,
        status: 'completed',
        method: 'credit_card',
        date: new Date().toISOString().split('T')[0],
        dueDate: paymentData.dueDate
      };
      
      setPayments([newPayment, ...payments]);
      setPaymentDialog(false);
      setSnackbar({ open: true, message: 'Payment processed successfully!', severity: 'success' });
      
      // Reset form
      setPaymentData({
        tenantId: '',
        propertyId: '',
        amount: '',
        type: 'rent',
        dueDate: '',
        description: ''
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Payment failed. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'credit_card': return <CreditCardIcon />;
      case 'bank_transfer': return <BankIcon />;
      case 'cash': return <PaymentIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const handleAddPayment = async () => {
    try {
      if (!paymentData.tenantId || !paymentData.amount) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      console.log('💰 Creating payment:', paymentData);

      await api.post(`/api/tenant-portal/tenant/${paymentData.tenantId}/payments`, {
        amount: parseFloat(paymentData.amount),
        method: paymentData.method,
        description: paymentData.description,
        status: paymentData.status
      });

      setSnackbar({ open: true, message: 'Payment created successfully!', severity: 'success' });
      setPaymentDialog(false);
      setPaymentData({
        tenantId: '',
        amount: '',
        method: 'Online',
        description: 'Rent payment',
        status: 'pending'
      });
      fetchPaymentData();
    } catch (error) {
      console.error('Error creating payment:', error);
      setSnackbar({ open: true, message: 'Failed to create payment', severity: 'error' });
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment({ ...payment });
    setEditPaymentDialog(true);
  };

  const handleUpdatePayment = async () => {
    try {
      if (!editingPayment?.id || !editingPayment?.tenantId) {
        setSnackbar({ open: true, message: 'Payment information not available', severity: 'error' });
        return;
      }

      console.log('📝 Updating payment:', editingPayment.id);

      await api.put(`/api/tenant-portal/tenant/${editingPayment.tenantId}/payments/${editingPayment.id}`, {
        amount: parseFloat(editingPayment.amount),
        method: editingPayment.method,
        description: editingPayment.description,
        status: editingPayment.status
      });

      setSnackbar({ open: true, message: 'Payment updated successfully!', severity: 'success' });
      setEditPaymentDialog(false);
      setEditingPayment(null);
      fetchPaymentData();
    } catch (error) {
      console.error('Error updating payment:', error);
      setSnackbar({ open: true, message: 'Failed to update payment', severity: 'error' });
    }
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setDeletePaymentDialog(true);
  };

  const handleDeletePayment = async () => {
    try {
      if (!paymentToDelete?.id || !paymentToDelete?.tenantId) {
        setSnackbar({ open: true, message: 'Payment information not available', severity: 'error' });
        return;
      }

      console.log('🗑️ Deleting payment:', paymentToDelete.id);

      await api.delete(`/api/tenant-portal/tenant/${paymentToDelete.tenantId}/payments/${paymentToDelete.id}`);

      setSnackbar({ open: true, message: 'Payment deleted successfully!', severity: 'success' });
      setDeletePaymentDialog(false);
      setPaymentToDelete(null);
      fetchPaymentData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      setSnackbar({ open: true, message: 'Failed to delete payment', severity: 'error' });
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Pending';
    try {
      if (date._seconds) {
        return new Date(date._seconds * 1000).toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (error) {
      return 'Pending';
    }
  };

  const exportPayments = () => {
    // Mock CSV export
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Tenant,Property,Amount,Type,Status,Date\n"
      + payments.map(p => `${p.tenantName},${p.property},${p.amount},${p.type},${p.status},${p.date}`).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "payments.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSnackbar({ open: true, message: 'Payments exported successfully!', severity: 'success' });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          Payment System
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setPaymentDialog(true)}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Process' : 'Process Payment'}
        </Button>
      </Box>

      {/* Financial Overview Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography 
                    variant="h5"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    ${financialData.totalRevenue.toLocaleString()}
                  </Typography>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: { xs: 28, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Collected
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color="success.main"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    ${financialData.collectedAmount.toLocaleString()}
                  </Typography>
                </Box>
                <PaymentIcon color="success" sx={{ fontSize: { xs: 28, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Outstanding
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color="error.main"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    ${financialData.outstandingAmount.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon color="error" sx={{ fontSize: { xs: 28, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Payment Rate
                  </Typography>
                  <Typography 
                    variant="h5"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {Math.round((financialData.collectedAmount / financialData.totalRevenue) * 100)}%
                  </Typography>
                </Box>
                <SecurityIcon color="primary" sx={{ fontSize: { xs: 28, sm: 40 } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: { xs: 2, sm: 3 } }}
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab label="Payments" />
        <Tab label="Analytics" />
        <Tab label="Settings" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {payments.filter(p => p.isOverdue).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Overdue Payments ({payments.filter(p => p.isOverdue).length})
              </Typography>
              <Typography variant="body2">
                {payments.filter(p => p.isOverdue).map(p => 
                  `${p.tenantName}: $${p.amount}${p.lateFee ? ` + $${p.lateFee} late fee` : ''}`
                ).join(' • ')}
              </Typography>
            </Alert>
          )}

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Payment Records
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setPaymentDialog(true)}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'Add' : 'Add Payment'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportPayments}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
              >
                Export
              </Button>
            </Box>
          </Box>
          
          <TableContainer 
            component={Paper}
            sx={{ 
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Tenant
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>
                    Property
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>
                    Late Fee
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }}>
                    Method
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>
                    Date
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 } }}>
                      {payment.tenantName}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>
                      {payment.property}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 }, fontWeight: 'bold' }}>
                      ${payment.amount}
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>
                      <Chip 
                        label={payment.type} 
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 } }}>
                      <Chip 
                        label={payment.isOverdue ? 'Overdue' : payment.status} 
                        color={getStatusColor(payment.isOverdue ? 'overdue' : payment.status)}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>
                      {payment.lateFee ? `$${payment.lateFee}` : '-'}
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getMethodIcon(payment.method)}
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {payment.method.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>
                      {formatDate(payment.date)}
                    </TableCell>
                    <TableCell align="center" sx={{ py: { xs: 0.5, sm: 1 } }}>
                      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'center' }}>
                        <Tooltip title="Edit Payment">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditPayment(payment)}
                            aria-label="Edit payment"
                          >
                            <EditIcon fontSize={isMobile ? 'small' : 'small'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Payment">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(payment)}
                            aria-label="Delete payment"
                          >
                            <DeleteIcon fontSize={isMobile ? 'small' : 'small'} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Monthly Revenue Trends</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={financialData.monthlyTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip />
                    <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Revenue" />
                    <Line type="monotone" dataKey="expenses" stroke="#82ca9d" name="Expenses" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Payment Methods</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={financialData.paymentMethods}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {financialData.paymentMethods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Payment Settings</Typography>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Stripe Integration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Stripe is configured and ready for processing payments. 
                  All transactions are encrypted and secure.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Payment Reminders</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Automated payment reminders are sent 3 days before due date.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Late Fee Policy</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Late fees of $50 are applied after 5 days past due date.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <Dialog 
        open={paymentDialog} 
        onClose={() => setPaymentDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Process Payment</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select
                  value={paymentData.tenantId}
                  onChange={(e) => setPaymentData({ ...paymentData, tenantId: e.target.value })}
                >
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.name} - {tenant.property}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Property</InputLabel>
                <Select
                  value={paymentData.propertyId}
                  onChange={(e) => setPaymentData({ ...paymentData, propertyId: e.target.value })}
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                InputProps={{
                  startAdornment: '$'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={paymentData.type}
                  onChange={(e) => setPaymentData({ ...paymentData, type: e.target.value })}
                >
                  <MenuItem value="rent">Rent</MenuItem>
                  <MenuItem value="deposit">Deposit</MenuItem>
                  <MenuItem value="late_fee">Late Fee</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={paymentData.dueDate}
                onChange={(e) => setPaymentData({ ...paymentData, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={paymentData.description}
                onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setPaymentDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={processPayment} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {loading ? 'Processing...' : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Manually add a payment record for any tenant.
            </Typography>
          </Alert>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tenant</InputLabel>
            <Select
              value={paymentData.tenantId}
              label="Tenant"
              onChange={(e) => setPaymentData({ ...paymentData, tenantId: e.target.value })}
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.firstName} {tenant.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={paymentData.amount}
            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
            margin="normal"
            InputProps={{
              startAdornment: <Typography>$</Typography>
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={paymentData.status}
              label="Status"
              onChange={(e) => setPaymentData({ ...paymentData, status: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            value={paymentData.description}
            onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setPaymentDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Add Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog 
        open={editPaymentDialog} 
        onClose={() => setEditPaymentDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Edit Payment</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Tenant"
            value={editingPayment?.tenantName || ''}
            margin="normal"
            disabled
            helperText="Tenant cannot be changed"
          />
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={editingPayment?.amount || ''}
            onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })}
            margin="normal"
            InputProps={{
              startAdornment: <Typography>$</Typography>
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={editingPayment?.status || 'pending'}
              label="Status"
              onChange={(e) => setEditingPayment({ ...editingPayment, status: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            value={editingPayment?.description || ''}
            onChange={(e) => setEditingPayment({ ...editingPayment, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setEditPaymentDialog(false);
              setEditingPayment(null);
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdatePayment} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Update Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Payment Confirmation Dialog */}
      <Dialog
        open={deletePaymentDialog}
        onClose={() => setDeletePaymentDialog(false)}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Delete Payment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Are you sure you want to delete this payment record? This action cannot be undone.
            </Typography>
          </Alert>
          {paymentToDelete && (
            <Box>
              <Typography variant="body1">
                <strong>Tenant:</strong> {paymentToDelete.tenantName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Amount:</strong> ${paymentToDelete.amount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {formatDate(paymentToDelete.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {paymentToDelete.status}
              </Typography>
              {paymentToDelete.description && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Description:</strong> {paymentToDelete.description}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setDeletePaymentDialog(false);
              setPaymentToDelete(null);
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePayment} 
            variant="contained" 
            color="error"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Delete Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentSystem; 